import { SiteMotionButton } from './SiteMotion';
import { trackEvent } from '../lib/analytics';
import React from 'react';
import { openCookieSettings } from '../lib/consent';
import { Link } from 'react-router';
import parrotIcon from '../assets/polypdf_icon-96.png';
import DirectCheckoutLink from './DirectCheckoutLink';
import { buyPath, canonicalPagePath } from '../lib/attribution';
import { DOWNLOADS } from '../lib/platform';
import { landingPages } from '../lib/landingPages';
import { useCommercialOffer } from '../lib/useCommercialOffer';

export const footerLinkGroups = Object.freeze([
  Object.freeze({
    label: 'Workflows',
    links: Object.freeze([
      Object.freeze({ to: landingPages.revisionPackages.path, label: 'Revision Packages' }),
      Object.freeze({ to: landingPages.pdfTakeoffSoftware.path, label: 'PDF takeoff software' }),
      Object.freeze({ to: landingPages.measurePdfOnMac.path, label: 'Measure a PDF on Mac' }),
      Object.freeze({ to: landingPages.constructionPdfMarkup.path, label: 'Construction PDF markup' }),
      Object.freeze({ to: landingPages.visualSearchPdfCount.path, label: 'PDF symbol counting' }),
      Object.freeze({ to: landingPages.comparePdfDrawings.path, label: 'Compare PDF drawings' }),
      Object.freeze({ to: '/pdf-measuring-tool', label: 'PDF measuring tool' }),
      Object.freeze({ to: '/electrical-takeoff-software', label: 'Electrical takeoff' }),
      Object.freeze({ to: '/flooring-takeoff-software', label: 'Flooring takeoff' })
    ])
  }),
  Object.freeze({
    label: 'Product',
    links: Object.freeze([
      Object.freeze({
        to: buyPath('website_footer'),
        label: 'Buy PolyPDF Pro',
        checkoutSource: 'website_footer'
      }),
      Object.freeze({ to: '/windows', label: 'PolyPDF for Windows' }),
      Object.freeze({ to: '/versions', label: 'Version history' }),
      Object.freeze({ to: '/blog', label: 'Guides & reference' }),
      Object.freeze({ to: '/build-a-plugin', label: 'Build a plugin' }),
      Object.freeze({ to: '/teams/guide', label: 'Teams setup guide' }),
      Object.freeze({ to: '/bluebeam-alternative', label: 'Compare with Bluebeam' }),
      Object.freeze({ to: '/samples', label: 'Sample files' }),
      Object.freeze({ to: '/tools', label: 'PDF tools' })
    ])
  }),
  Object.freeze({
    label: 'Help & company',
    links: Object.freeze([
      Object.freeze({ to: '/support', label: 'Support' }),
      Object.freeze({ to: '/product-facts', label: 'Product facts' }),
      Object.freeze({ to: '/feature-requests', label: 'Feature requests' }),
      Object.freeze({ to: '/account', label: 'License account' }),
      Object.freeze({ to: '/refund', label: 'Refund policy' }),
      Object.freeze({ to: '/terms', label: 'Terms of use' }),
      Object.freeze({ to: '/privacy', label: 'Privacy policy' }),
      Object.freeze({ to: '/cookies', label: 'Cookie policy' }),
      Object.freeze({ to: '/accessibility', label: 'Accessibility' })
    ])
  })
]);

const trackDownload = (platform) => {
  if (typeof window === 'undefined') return;
  const properties = { source: 'website_footer', platform: platform.key };
  trackEvent('download_click', properties);
};

const SiteFooter = () => {
  const offer = useCommercialOffer();
  return (
  <footer className="site-footer" data-site-footer>
    <div className="container site-footer-shell">
      <div className="site-footer-intro">
        <Link className="site-footer-logo" to="/" aria-label="PolyPDF home">
          <img src={parrotIcon} alt="" loading="lazy" width="96" height="96" />
          <span>PolyPDF</span>
        </Link>
        <p>Measure, count, compare, manage revisions, and mark up PDF drawings on Mac and Windows—without a yearly bill.</p>
        <div className="site-footer-downloads" aria-label="PolyPDF downloads">
          {Object.values(DOWNLOADS).map((platform) => (
            <a
              key={platform.key}
              href={platform.url}
              download
              onClick={() => trackDownload(platform)}
            >
              Download for {platform.name}
            </a>
          ))}
        </div>
      </div>

      <nav className="site-footer-nav" aria-label="Footer navigation">
        {footerLinkGroups.map((group) => (
          <section className="site-footer-group" key={group.label} aria-labelledby={`footer-${group.label.toLowerCase().replaceAll(/[^a-z]+/g, '-')}`}>
            <h2 id={`footer-${group.label.toLowerCase().replaceAll(/[^a-z]+/g, '-')}`}>{group.label}</h2>
            <ul>
              {group.links.map((link) => (
                <li key={link.to}>
                  {link.checkoutSource ? (
                    <DirectCheckoutLink
                      offer={offer}
                      source={link.checkoutSource}
                      pageVariant="footer"
                    >
                      {link.label}
                    </DirectCheckoutLink>
                  ) : (
                    <Link to={canonicalPagePath(link.to)}>{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
    </div>

    <div className="container site-footer-bottom">
      <p>&copy; 2026 Euclidean Software LLC. All rights reserved.</p>
      <p>PolyPDF is desktop software for macOS and Windows.</p>
      <button type="button" className="cookie-settings-button" onClick={openCookieSettings}>Cookie settings / Do not sell or share</button>
      <SiteMotionButton />
    </div>
  </footer>
  );
};

export default SiteFooter;
